import { useRef, useState, type MouseEvent, type PointerEvent } from 'react'
import { useLatestRef } from './useLatestRef'

interface Gesture {
  id: number
  x: number
  y: number
  lastX: number
  lastT: number
  vx: number
  horizontal: boolean | null
}

/**
 * Tracks a horizontal drag that follows the pointer. Spread `handlers` on the
 * element and give it `touch-pan-y`, so vertical touches still scroll the page
 * (the browser cancels the pointer) while horizontal ones reach us. `dragX` is
 * the live offset in px, null when not dragging. `onEnd` gets the final offset
 * and the release velocity in px/ms. The click that ends a drag is swallowed so
 * a day under the finger doesn't open.
 */
export function useHorizontalDrag({ onStart, onEnd }: {
  onStart?: () => void
  onEnd: (dx: number, velocity: number) => void
}) {
  const [dragX, setDragX] = useState<number | null>(null)
  const gesture = useRef<Gesture | null>(null)
  const suppressClick = useRef(false)
  const onStartRef = useLatestRef(onStart)
  const onEndRef = useLatestRef(onEnd)

  function onPointerDown(e: PointerEvent<HTMLElement>) {
    if (e.pointerType === 'mouse' && e.button !== 0) return
    gesture.current = { id: e.pointerId, x: e.clientX, y: e.clientY, lastX: e.clientX, lastT: e.timeStamp, vx: 0, horizontal: null }
  }

  function onPointerMove(e: PointerEvent<HTMLElement>) {
    const g = gesture.current
    if (!g || g.id !== e.pointerId || g.horizontal === false) return
    const dx = e.clientX - g.x
    if (g.horizontal === null) {
      const dy = e.clientY - g.y
      if (Math.abs(dx) + Math.abs(dy) < 8) return
      g.horizontal = Math.abs(dx) > Math.abs(dy)
      if (!g.horizontal) return
      // Captured only once the drag is horizontal: capturing on pointerdown would
      // retarget a plain tap's click away from the day cell.
      e.currentTarget.setPointerCapture(e.pointerId)
      onStartRef.current?.()
    }
    const dt = e.timeStamp - g.lastT
    if (dt > 0) g.vx = (e.clientX - g.lastX) / dt
    g.lastX = e.clientX
    g.lastT = e.timeStamp
    setDragX(dx)
  }

  function finish(e: PointerEvent<HTMLElement>, cancelled: boolean) {
    const g = gesture.current
    if (!g || g.id !== e.pointerId) return
    gesture.current = null
    if (!g.horizontal) return
    suppressClick.current = true
    setTimeout(() => { suppressClick.current = false }, 0)
    setDragX(null)
    const stale = e.timeStamp - g.lastT > 100
    onEndRef.current(cancelled ? 0 : e.clientX - g.x, cancelled || stale ? 0 : g.vx)
  }

  function onClickCapture(e: MouseEvent<HTMLElement>) {
    if (!suppressClick.current) return
    e.preventDefault()
    e.stopPropagation()
  }

  return {
    dragX,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: (e: PointerEvent<HTMLElement>) => finish(e, false),
      onPointerCancel: (e: PointerEvent<HTMLElement>) => finish(e, true),
      onClickCapture,
    },
  }
}
